import * as React from 'react';
import { useState, useEffect } from 'react';

// Note: defaults now live in the app package.
// Simple deep merge utility suitable for config/state trees
function isObject(v) {
    return typeof v === 'object' && v !== null && !Array.isArray(v);
}
function deepMerge(...objects) {
    const result = {};
    for (const obj of objects) {
        if (!obj)
            continue;
        for (const [key, value] of Object.entries(obj)) {
            const prev = result[key];
            if (Array.isArray(value)) {
                result[key] = Array.isArray(prev) ? [...prev, ...value] : [...value];
            }
            else if (isObject(value) && isObject(prev)) {
                result[key] = deepMerge(prev, value);
            }
            else if (isObject(value)) {
                result[key] = deepMerge({}, value);
            }
            else {
                result[key] = value;
            }
        }
    }
    return result;
}
const ApplicationContext = React.createContext({});
const ApplicationDispatchContext = React.createContext(null);
function push(issues, path, message) {
    issues.push({ path, message });
}
function validateAppContext(value, basePath = 'app') {
    const issues = [];
    const v = value;
    if (v == null || typeof v !== 'object') {
        push(issues, basePath, 'must be an object');
        return { ok: issues.length === 0, issues };
    }
    // No setup/config validation here; apps provide their own typing/validation.
    if (v.ui) {
        if (typeof v.ui !== 'object')
            push(issues, basePath + '.ui', 'must be an object');
        else {
            const u = v.ui;
            if (u.transitions != null) {
                if (!Array.isArray(u.transitions))
                    push(issues, basePath + '.ui.transitions', 'must be an array');
                else
                    u.transitions.forEach((t, i) => {
                        const p = `${basePath}.ui.transitions[${i}]`;
                        if (typeof t !== 'object')
                            push(issues, p, 'must be an object');
                        else if (typeof t.run !== 'function')
                            push(issues, p + '.run', 'is required function');
                    });
            }
            if (u.state != null && typeof u.state !== 'object')
                push(issues, basePath + '.ui.state', 'must be an object');
        }
    }
    return { ok: issues.length === 0, issues };
}
// Note: No internal defaultAppContext; apps should supply defaults via ApplicationProvider.defaults

function useApplicationContext() {
    var _a, _b;
    const context = React.useContext(ApplicationContext);
    const dispatch = React.useContext(ApplicationDispatchContext);
    // create convenience/compatibility fields without mutating context
    const derived = {
        value: (context === null || context === void 0 ? void 0 : context.title) || '',
        appBar: { title: context === null || context === void 0 ? void 0 : context.appBarTitle, display: context === null || context === void 0 ? void 0 : context.appBarDisplay },
        navBar: {
            links: (_a = context === null || context === void 0 ? void 0 : context.navBar) === null || _a === void 0 ? void 0 : _a.links,
            display: (_b = context === null || context === void 0 ? void 0 : context.navBar) === null || _b === void 0 ? void 0 : _b.display,
            render: () => {
                var _a;
                return ((_a = context === null || context === void 0 ? void 0 : context.navBar) === null || _a === void 0 ? void 0 : _a.render) ? context.navBar.render() : null;
            }
        },
        topNav: context === null || context === void 0 ? void 0 : context.navLinks,
        setTitle: (title) => dispatch === null || dispatch === void 0 ? void 0 : dispatch({ type: 'SET_TITLE', title }),
        setNavLinks: (links) => dispatch === null || dispatch === void 0 ? void 0 : dispatch({ type: 'SET_NAV_LINKS', links }),
        toggleAppBar: (display) => dispatch === null || dispatch === void 0 ? void 0 : dispatch({ type: 'TOGGLE_APP_BAR', display }),
        toggleNavBar: (display) => dispatch === null || dispatch === void 0 ? void 0 : dispatch({ type: 'TOGGLE_NAV_BAR', display }),
        setBrand: ({ name, logo }) => dispatch === null || dispatch === void 0 ? void 0 : dispatch({ type: 'SET_BRAND', name, logo }),
        setTheme: (theme) => dispatch === null || dispatch === void 0 ? void 0 : dispatch({ type: 'SET_THEME', theme }),
        update: (patch) => dispatch === null || dispatch === void 0 ? void 0 : dispatch({ type: 'MERGE', payload: patch }),
    };
    return Object.assign({}, context, derived);
}

/**
 * breakpoints: sm, md, lg
 *
 * @returns
 */
function useResponsive() {
    const [small, setSmall] = useState(window.innerWidth <= 768);
    const [medium, setMedium] = useState(window.innerWidth > 768 && window.innerWidth < 992);
    const [large, setLarge] = useState(window.innerWidth > 992);
    useEffect(() => {
        const smallMql = window.matchMedia("(max-width: 767.98px)");
        const mediumMql = window.matchMedia("(min-width: 768px) and (max-width: 991.98px)");
        const largeMql = window.matchMedia("(min-width: 992px)");
        function smallScreenTest(e) {
            if (e.matches) {
                setSmall(true);
                setMedium(false);
                setLarge(false);
            }
            else {
                setSmall(false);
            }
        }
        function mediumScreenTest(e) {
            if (e.matches) {
                setSmall(false);
                setMedium(true);
                setLarge(false);
            }
            else {
                setMedium(false);
            }
        }
        function largeScreenTest(e) {
            if (e.matches) {
                setSmall(false);
                setMedium(false);
                setLarge(true);
            }
            else {
                setLarge(false);
            }
        }
        smallMql.addEventListener("change", smallScreenTest);
        mediumMql.addEventListener("change", mediumScreenTest);
        largeMql.addEventListener("change", largeScreenTest);
        return () => {
            smallMql.removeEventListener("change", smallScreenTest);
            mediumMql.removeEventListener("change", mediumScreenTest);
            largeMql.removeEventListener("change", largeScreenTest);
        };
    }, []);
    return [small, medium, large];
}

function ApplicationProvider({ value, defaults, children }) {
    const initial = React.useMemo(() => deepMerge({}, defaults !== null && defaults !== void 0 ? defaults : {}, value !== null && value !== void 0 ? value : {}), [defaults, value]);
    function reducer(state, action) {
        var _a, _b, _c, _d, _e, _f, _g;
        switch (action.type) {
            case 'SET_TITLE':
                return Object.assign(Object.assign({}, state), { title: action.title });
            case 'SET_NAV_LINKS':
                return Object.assign(Object.assign({}, state), { navBar: Object.assign(Object.assign({}, state.navBar), { links: action.links }) });
            case 'TOGGLE_APP_BAR':
                return Object.assign(Object.assign({}, state), { appBar: Object.assign(Object.assign({}, state.appBar), { display: (_a = action.display) !== null && _a !== void 0 ? _a : !state.appBar.display }) });
            case 'TOGGLE_NAV_BAR':
                return Object.assign(Object.assign({}, state), { navBar: Object.assign(Object.assign({}, state.navBar), { display: (_b = action.display) !== null && _b !== void 0 ? _b : !state.navBar.display }) });
            case 'SET_BRAND':
                return Object.assign(Object.assign({}, state), { brandName: (_c = action.name) !== null && _c !== void 0 ? _c : state.brandName, brandLogo: (_d = action.logo) !== null && _d !== void 0 ? _d : state.brandLogo });
            case 'SET_THEME':
                return Object.assign(Object.assign({}, state), { theme: Object.assign(Object.assign({}, ((_e = state.theme) !== null && _e !== void 0 ? _e : {})), ((_f = action.theme) !== null && _f !== void 0 ? _f : {})) });
            case 'MERGE':
                return deepMerge({}, state, (_g = action.payload) !== null && _g !== void 0 ? _g : {});
            default:
                return state;
        }
    }
    const [state, dispatch] = React.useReducer(reducer, initial);
    return (React.createElement(ApplicationDispatchContext.Provider, { value: dispatch },
        React.createElement(ApplicationContext.Provider, { value: state }, children)));
}

export { ApplicationContext, ApplicationDispatchContext, ApplicationProvider, deepMerge, useApplicationContext, useResponsive, validateAppContext };
//# sourceMappingURL=index.js.map
